/* eslint-disable class-methods-use-this */
import React, { type ReactNode } from 'react';

import { Error } from '@mui/icons-material';
import { Button, Dialog } from '@mui/material';
import axios, { type AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';

import { HTTP_REQUEST_ERROR, NETWORK_ERROR, UNKNOWN_ERROR } from './whiteboard-errors';
import { getUrlParams, getUrls } from '../../utils/url';

const Container = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 16px;
    padding: 24px;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ErrorIcon = styled(Error)`
    font-size: 32px;
    fill: red;
`;

const Title = styled.h1`
    font-size: 28px;
    font-weight: 400;
    margin-top: 0.25rem;
    margin-bottom: 1.5rem;
`;

const Message = styled.div`
    font-size: 16px;
    margin-bottom: 1rem;
`;

const StyledButton = styled(Button)`
    margin-left: auto;
`;

type Props = {
    children: ReactNode;
    t: Function;
};

type State = {
    error: Error | string;
    code: string;
};

class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { error: '', code: '' };
    }

    static getDerivedStateFromError(error: Error) {
        const pattern = /^\d{4}/;

        if (pattern.test(error.message)) {
            const code = error.message.substring(0, 4);
            return { error, code };
        }

        // Update state so the next render will show the fallback UI.
        return { error, code: UNKNOWN_ERROR };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.captureError(error.toString(), errorInfo.componentStack);

        console.error('Uncaught error:', error, errorInfo);
    }

    /**
     * Sends error and metadata to Class for analysis and
     * includes as much info as possible for debugging.
     */
    private captureError = (errorMessage: string, stackTrace?: string) => {
        const { api } = getUrls();

        try {
            const params = {
                error_message: errorMessage || 'WhiteboardError',
                stack_trace: stackTrace,
                url: window.location.origin + window.location.pathname,
                query_params: this.getQueryParams()
            };

            axios.post(`${api}/v1/whiteboard/error`, params);
        } catch (error) {
            console.error('Error capture failed:', error);
        }
    };

    /**
     * Gets the query params for error debugging and decodes the
     * user token (JWT) and reattaches it to the return object.
     */
    private getQueryParams() {
        const urlParams = getUrlParams();

        if (urlParams.token) {
            try {
                urlParams.token = jwtDecode(urlParams.token);
            } catch (err) {
                console.warn('Token is not a valid jwt');
                urlParams.token = 'redacted';
            }
        }

        return urlParams;
    }

    private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
        const error = event.reason;

        try {
            if (error.toString().indexOf('TypeError: i[e] is not a function') !== -1) {
                return;
            }
        } catch (err) {}

        if (axios.isAxiosError(error)) {
            this.handleAxiosError(error);
            return;
        }

        this.captureError(error.toString(), error.stack);

        const pattern = /^\d{4}/;
        if (pattern.test(error.message)) {
            this.setState({ error, code: error.message.substring(0, 4) });
        } else {
            this.setState({ error, code: UNKNOWN_ERROR.toString() });
        }
    };

    /**
     * Sets the proper error code for axios non-2xx responses
     */
    private handleAxiosError = (error: AxiosError) => {
        const errorMessage: string = error.message;
        const stackTrace: string = JSON.stringify(error.toJSON(), null, 4);
        let code;

        if (error.response) {
            code = HTTP_REQUEST_ERROR;
        } else if (error.request) {
            code = NETWORK_ERROR;
        } else {
            code = UNKNOWN_ERROR;
        }

        this.captureError(errorMessage, stackTrace);
        this.setState({ error, code: code.toString() });
    };

    componentDidMount() {
        // Add an event listener to the window to catch unhandled promise rejections & stash the error in the state
        window.addEventListener('unhandledrejection', this.promiseRejectionHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('unhandledrejection', this.promiseRejectionHandler);
    }

    render() {
        if (this.state.error) {
            return this.errorSplash();
        }

        return this.props.children;
    }

    private errorSplash() {
        return (
            <Dialog open={true} fullWidth={true}>
                <Container>
                    <ErrorIcon />
                    <FlexContainer>
                        <Title>{this.props.t('somethingWentWrong')}</Title>
                        <Message>{this.props.t('errorMessage')}</Message>
                        <Message>{this.props.t('errorCode', { code: this.state.code })}</Message>
                        <StyledButton
                            onClick={() => {
                                window.location.reload();
                            }}
                            color="primary"
                            variant="contained"
                        >
                            {this.props.t('refresh')}
                        </StyledButton>
                    </FlexContainer>
                </Container>
            </Dialog>
        );
    }
}

export default withTranslation('common')(ErrorBoundary);
